import React from "react";
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha,
} from "react-google-recaptcha-v3";
import { get } from "lodash";
import { Formik } from "formik";
import { navigate } from "gatsby";
import NumberFormat from "react-number-format";
import axios from "axios";
import { GOOGLE_RECAPTCHA_SITE_KEY, BASE_PATH } from "../config";
import useFirebase from "../useFirebase";
import { getFirstName, getAxiosErrorMessage } from "../utils";
import { EMAIL_REGEX } from "../constants";

export default function SignupForm({ location }) {
  return (
    <GoogleReCaptchaProvider reCaptchaKey={GOOGLE_RECAPTCHA_SITE_KEY}>
      <SignupFormBody location={location} />
    </GoogleReCaptchaProvider>
  );
}

function SignupFormBody({ location }) {
  const redirectTo = get(location, "state.redirectTo");
  const ruid = getRuid(location);
  const firebase = useFirebase();
  const { executeRecaptcha } = useGoogleReCaptcha();

  return (
    <div>
      <Formik
        initialValues={{
          name: "",
          phoneNumber: "",
          email: "",
          password: "",
        }}
        validate={({ name, phoneNumber, email, password }) => {
          let errors = {};
          if (!name) {
            errors.name = "Please enter your name";
          } else if (name.length > 50) {
            errors.name = "Name cannot exceed 50 characters";
          }
          if (
            phoneNumber &&
            /((\(\d{3}\) ?)|(\d{3}-))?\d{3}-\d{4}/.test(phoneNumber) === false
          ) {
            errors.phoneNumber = "Please enter a valid phone number";
          }
          if (EMAIL_REGEX.test(email) === false) {
            errors.email = "Please enter a valid email";
          }
          if (password.length < 8) {
            errors.password = "Password must be at least 8 characters";
          }
          return errors;
        }}
        onSubmit={(values, actions) => {
          actions.setStatus(null);
          let userData;
          let couponCode;
          executeRecaptcha("SIGN_UP")
            .then((recaptchaToken) => {
              return axios.post(`${BASE_PATH}/createUser`, {
                ...values,
                recaptchaToken,
                ruid,
              });
            })
            .then(({ data }) => {
              userData = data.user;
              couponCode = data.couponCode;
              return firebase
                .auth()
                .signInWithEmailAndPassword(values.email, values.password);
            })
            .then(() => {
              navigate(redirectTo || `/account/favorites/`, {
                state: {
                  snackbarMessages: [
                    `Welcome to till, ${getFirstName(userData)}!`,
                  ],
                  couponCode,
                },
              });
            })
            .catch((err) => {
              console.log(err);
              actions.setStatus(getAxiosErrorMessage(err));
              actions.setSubmitting(false);
            });
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          status,
        }) => {
          function renderStatus() {
            if (status) {
              return (
                <div className="status-container text-center error">
                  <span>{status}</span>
                </div>
              );
            } else if (ruid) {
              return (
                <div className="status-container text-center">
                  <span>
                    Looks like you've been referred here by a friend! For an
                    overview on how till works, please{" "}
                    <a
                      href={`https://usetill.com/how-till-works/`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: "inherit",
                        font: "inherit",
                        textDecoration: "underline",
                      }}
                    >
                      click here
                    </a>
                    .
                  </span>
                </div>
              );
            } else if (location.state && location.state.redirectTo) {
              return (
                <div className="status-container text-center">
                  <span>Please create an account to continue</span>
                </div>
              );
            }
            return undefined;
          }
          function renderPhoneNumberMsg() {
            if (errors.phoneNumber && touched.phoneNumber) {
              return <div className="error-msg">{errors.phoneNumber}</div>;
            }
            return (
              <div className="helper-text">
                Enter a phone number to receive text alerts for deliveries.
                Standard messaging rates apply.
              </div>
            );
          }
          return (
            <>
              {renderStatus()}
              <form onSubmit={handleSubmit}>
                <input
                  name="name"
                  className={`form-input${
                    touched.name && errors.name ? " invalid" : ""
                  }`}
                  type="text"
                  placeholder="Full name"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.name}
                />
                {touched.name && errors.name && (
                  <div className="error-msg">{errors.name}</div>
                )}
                <NumberFormat
                  format="(###) ###-####"
                  mask="_"
                  name="phoneNumber"
                  className={`form-input${
                    touched.phoneNumber && errors.phoneNumber ? " invalid" : ""
                  }`}
                  type="tel"
                  placeholder="Phone number (optional)"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.phoneNumber}
                />
                {renderPhoneNumberMsg()}
                <input
                  name="email"
                  className={`form-input${
                    touched.email && errors.email ? " invalid" : ""
                  }`}
                  type="email"
                  placeholder="Email"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.email}
                />
                {touched.email && errors.email && (
                  <div className="error-msg">{errors.email}</div>
                )}
                <input
                  name="password"
                  className={`form-input${
                    touched.password && errors.password ? " invalid" : ""
                  }`}
                  type="password"
                  placeholder="Password"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.password}
                />
                {touched.password && errors.password && (
                  <div className="error-msg">{errors.password}</div>
                )}
                <button
                  type="submit"
                  className={`btn primary-btn${
                    isSubmitting ? " disabled" : ""
                  }`}
                  disabled={isSubmitting}
                  style={{ width: "100%" }}
                >
                  {isSubmitting ? (
                    <span className="saving">
                      SUBMITTING<span>.</span>
                      <span>.</span>
                      <span>.</span>
                    </span>
                  ) : (
                    <span>CREATE ACCOUNT</span>
                  )}
                </button>
              </form>
            </>
          );
        }}
      </Formik>
    </div>
  );
}

function getRuid(location) {
  if (location && location.search) {
    const split = location.search.split("=");
    if (split[0] === "?ruid" && split[1]) {
      return split[1];
    }
  }
  return "";
}
